/* eslint-disable consistent-return, no-useless-escape */
import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { pick, map, includes, values } from 'lodash';

import AnnotationTemplates from '../AnnotationTemplates/AnnotationTemplates';
import { FeatureLineString, FeatureCollectionPoints } from '../SchemaUtilities/GeoJSONSchema';

import requiredIf from '../../modules/schema-conditional-required';

const AnnotationTemplatesSchema = AnnotationTemplates.schema.omit('owner', 'deleted');
AnnotationTemplatesSchema.extend({ _id: String });

const URL_EXPRESSION = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;
export const URL_REGEX = new RegExp(URL_EXPRESSION);

const YOUTUBE_URL_EXPRESSION = /^(https?\:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
export const YOUTUBE_URL_REGEX = new RegExp(YOUTUBE_URL_EXPRESSION);

export const fechaAGpsEnMicrosegundos = () => {
  const fechaConHora = new Date();
  const fechaReferenciaGps = new Date('1980-01-06T00:00:00Z');
  const diferencia = fechaConHora.getTime() - fechaReferenciaGps.getTime();
  const diferenciaEnMicroSegundos = diferencia * 1000;
  return diferenciaEnMicroSegundos;
};

export const VIDEO_COORDINATE_DATA_TYPES = {
  POINT: 'point',
  LINE: 'line',
  KML: 'kml',
  CSV: 'csv',
  CSV_GO_PRO: 'csvGoPro',
  properties: {
    POINT: {
      name: 'POINT',
      value: 'point',
      humanValue: 'Point'
    },
    LINE: {
      name: 'LINE',
      value: 'line',
      humanValue: 'Line'
    },
    KML: {
      name: 'KML',
      value: 'kml',
      humanValue: 'KML File'
    },
    CSV: {
      name: 'CSV',
      value: 'csv',
      humanValue: 'CSV File'
    },
    CSV_GO_PRO: {
      name: 'CSV_GO_PRO',
      value: 'csvGoPro',
      humanValue: 'GoPro CSV File'
    }
  }
};

export const VIDEO_TYPES = {
  VIDEOWITHINFO: 'videowithinfo',
  VIDEOWITHOUTINFO: 'videowithoutinfo',
  properties: {
    VIDEOWITHINFO: {
      name: 'VIDEOWITHINFO',
      value: 'videowithinfo',
      humanValue: 'Video with header information'
    },
    VIDEOWITHOUTINFO: {
      name: 'VIDEOWITHOUTINFO',
      value: 'videowithoutinfo',
      humanValue: 'Video without header information'
    }
  }
};

export const VIDEO_OPTIONS = {
  OPT0: 'opt0',
  OPT1: 'opt1',
  OPT2: 'opt2',
  OPT3: 'opt3',
  OPT4: 'opt4',
  properties: {
    OPT0: {
      name: 'OPT0',
      value: 'opt0',
      humanValue: 'Start Position'
    },
    OPT1: {
      name: 'OPT1',
      value: 'opt1',
      humanValue: 'Device Position'
    },
    OPT2: {
      name: 'OPT2',
      value: 'opt2',
      humanValue: 'Device Position + Focus Point'
    },
    OPT3: {
      name: 'OPT3',
      value: 'opt3',
      humanValue: 'Covered Surface'
    },
    OPT4: {
      name: 'OPT4',
      value: 'opt4',
      humanValue: 'Projected Video'
    }
  }
};

export const POINT_OPTIONS = pick(VIDEO_OPTIONS, ['OPT0']);

export const LINE_OPTIONS = pick(VIDEO_OPTIONS, ['OPT0', 'OPT1']);

export const CSV_OPTIONS = pick(VIDEO_OPTIONS, ['OPT0', 'OPT1', 'OPT3', 'OPT4']);

export const CSV_DATA_TYPES = pick(VIDEO_COORDINATE_DATA_TYPES, ['CSV', 'CSV_GO_PRO']);
export const VIDEO_EXPORT_FORMATS = {
  PDF: 'PDF',
  KML: 'KML',
  GEOJSON: 'GeoJSON'
};

/**
 * Get the options allowed for each video coordinate data type.
 *
 * @param {boolean} onlyPoint - If video coordinate data type is Point.
 * @param {boolean} undefinedCoords - If coordinates are not defined.
 * @returns {object} The video options key-value pair for each coordinate data type.
 */
export const getOptionsForCoordinateDataType = (onlyPoint, undefinedCoords) => {
  switch (true) {
    case !onlyPoint && !undefinedCoords:
      return CSV_OPTIONS;
    case !onlyPoint && undefinedCoords:
      return LINE_OPTIONS;
    case onlyPoint:
      return POINT_OPTIONS;
    default:
      return POINT_OPTIONS;
  }
};

const Videos = new Mongo.Collection('Videos');

Videos.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Videos.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const tagSchema = new SimpleSchema({
  id: {
    type: String,
    label: 'The id of the associated tag'
  },
  text: {
    type: String,
    label: 'The text of the associated tag'
  }
});

const MapGMVSchema = new SimpleSchema({
  baseMaps: {
    type: Array,
    label: 'Array with id of base maps for geomotion video'
  },
  'baseMaps.$': {
    type: Object,
    label: 'The id of base map selected for geomotion video',
    blackbox: true
  },
  layersMaps: {
    type: Array,
    label: 'Array with id of layers maps for geomotion video'
  },
  'layersMaps.$': {
    type: Object,
    label: 'The id of layers map selected for geomotion video',
    blackbox: true
  }
});

Videos.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this video belongs to.'
  },
  project: {
    type: String,
    label: 'The ID of the project this video belongs to.'
  },
  createdAt: {
    type: String,
    label: 'The date this video was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this video was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  name: {
    type: String,
    label: 'A name to identify the video.'
  },
  description: {
    type: String,
    label: 'A description of the video.',
    optional: true
  },
  shared: {
    type: Boolean,
    label: 'Flag that checks if video is shared',
    optional: true,
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  sharedPrivate: {
    type: Boolean,
    label: 'Flag that checks if video is shared private',
    optional: true,
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  audience: {
    type: Array,
    label: 'Array of the users the video is shared with',
    optional: true,
    maxCount: 1,
    autoValue() {
      if (this.isInsert) return ['limited'];
    }
  },
  'audience.$': {
    type: String,
    label: 'Each user the video is shared with',
    allowedValues: ['all', 'limited', 'private', 'privateLimited']
  },
  url: {
    type: String,
    regEx: URL_REGEX,
    label: 'The url that points to the video in question in an external server'
  },
  geometryFileName: {
    type: String,
    label: 'The name of the kml file that contains the geometry forr the track',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition:
          this.field('coordinatesDataType').value === VIDEO_COORDINATE_DATA_TYPES.KML,
        schemaInstance: this
      });
    }
  },
  logFileName: {
    type: String,
    label: 'The file that contains the log',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          values(CSV_DATA_TYPES),
          this.field('coordinatesDataType').value
        ),
        schemaInstance: this
      });
    }
  },
  latLng0: {
    type: Array,
    label: 'The starting point of the flight'
  },
  'latLng0.$': {
    type: Number,
    label: 'The starting point of the flight'
  },
  noCoordinates: {
    type: Boolean,
    label: 'Flag that checks if corner coordinates are undefined'
  },
  option: {
    type: String,
    label: 'The projection option for the video item',
    optional: true,
    autoValue() {
      if (this.isInsert) return VIDEO_OPTIONS.OPT0;
    }
  },
  type: {
    type: String,
    label: 'The filming source of the video',
    allowedValues: map(VIDEO_TYPES.properties, 'value')
  },
  deleted: {
    type: String,
    label: 'The date the video was deleted.',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  tags: {
    type: Array,
    label: 'The tags associated to the video',
    optional: true
  },
  'tags.$': {
    type: tagSchema,
    label: 'The tags associated to the video'
  },
  track: {
    type: FeatureLineString,
    label: 'The line defined by the user',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition:
          this.field('coordinatesDataType').value !== VIDEO_COORDINATE_DATA_TYPES.POINT,
        schemaInstance: this
      });
    }
  },
  controlPoints: {
    type: FeatureCollectionPoints,
    label: 'The points used in the synchronization',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition:
          this.field('coordinatesDataType').value !== VIDEO_COORDINATE_DATA_TYPES.POINT,
        schemaInstance: this
      });
    }
  },
  coordinatesDataType: {
    type: String,
    label: 'The way of introducing the coordinates of the path',
    allowedValues: map(VIDEO_COORDINATE_DATA_TYPES.properties, 'value')
  },
  annotationTemplates: {
    type: Array,
    label: 'The different schemas for annotations',
    minCount: 1
  },
  'annotationTemplates.$': {
    type: AnnotationTemplatesSchema,
    label: 'The different schemas for annotations'
  },
  templatesId: {
    type: Array,
    label: 'The templates asociated to video',
    optional: true
  },
  'templatesId.$': {
    type: String,
    label: 'The id of the template'
  },
  timeGPSRecording: {
    type: String,
    label: 'The time GPS of recording video ',
    optional: true
  },
  geomotionMap: {
    type: MapGMVSchema,
    label: 'The base map, zoom and transparency for geomotion map',
    optional: true
  }
});

Videos.attachSchema(Videos.schema);

export default Videos;
